import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/hooks/use-notifications'
import { notificationHref } from '@/lib/notifications'
import { cn } from '@/lib/utils'
import type { Notification } from '@riverpark/types'

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data } = useNotifications()
  const markOne = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const items = data?.data ?? []
  const unreadCount = data?.meta.unreadCount ?? 0

  useEffect(() => {
    if (!open) return

    function close() {
      setOpen(false)
    }
    function onPointerDown(e: PointerEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) close()
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  function handleItemClick(item: Notification) {
    if (!item.readAt) markOne.mutate(item.id)
    const href = user ? notificationHref(item, user.role) : null
    if (href) {
      setOpen(false)
      navigate(href)
    }
  }

  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'relative rounded p-2 transition-colors duration-150',
          'hover:bg-cream-2 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/40',
          open && 'bg-cream-2',
        )}
      >
        <Bell size={20} className="text-muted-foreground" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-destructive" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 max-h-96 w-80 overflow-y-auto border border-border bg-card py-1 shadow-modal"
        >
          <div className="flex items-center justify-between border-b border-border-2 px-4 py-2.5">
            <p className="font-sans text-sm font-medium text-ink">Notifications</p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="font-sans text-xs text-primary hover:text-green-lt disabled:opacity-50"
              >
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="px-4 py-6 text-center font-sans text-sm text-muted-foreground">
              No notifications yet.
            </p>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                onClick={() => handleItemClick(item)}
                className={cn(
                  'block w-full border-b border-border-2 px-4 py-2.5 text-left transition-colors last:border-0 hover:bg-cream-2',
                  !item.readAt && 'bg-cream-2/50',
                )}
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="font-sans text-sm font-medium text-ink">{item.title}</span>
                  {!item.readAt && (
                    <span className="mt-1 size-1.5 shrink-0 rounded-full bg-primary" />
                  )}
                </span>
                <span className="mt-0.5 block font-sans text-xs text-muted-foreground">
                  {item.body}
                </span>
                <span className="mt-1 block font-sans text-[11px] text-muted-foreground/70">
                  {formatDateTime(item.createdAt)}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
